<template>
  <div class="autocomplete">
    <div class="autocomplete__box" :class="[inputClass, {'autocomplete__searching' : showResults}]">


      <div class="autocomplete__inputs">
        <input
          v-model="display"
          type="text"
          :placeholder="placeholder"
          :title="placeholder"
          :disabled="disableInput"
          @input="search"
          @keyup="search"
          @keydown.enter.prevent="enter"
          @keydown.tab="close"
          @keydown.up="up"
          @keydown.down="down"
          @keydown.esc="close"
          @focus="focus"
          @blur="blur"
          >
        <input :name="name" type="hidden" :value="value">

      </div>

      <span v-if="!isLoading && isEmpty" class="shevron" @click="focus"></span>

      <svg v-if="isLoading" class="autocomplete__icon animate-spin" xmlns="http://www.w3.org/2000/svg" viewBox="-18 172 450 450">
        <path fill="#999" d="M226.7 289a19.7 19.7 0 1 1-39.4 0v-96.6a19.7 19.7 0 0 1 39.4 0V289zm0 312.6a19.7 19.7 0 1 1-39.4 0V505a19.7 19.7 0 0 1 39.4 0v96.6zM99 377.3a19.7 19.7 0 0 1 0 39.3H2.4a19.7 19.7 0 0 1-19.7-19.7c0-10.9 8.8-19.6 19.7-19.6H99zm312.6 0a19.7 19.7 0 0 1 0 39.3H315a19.7 19.7 0 0 1-19.7-19.7c0-10.9 8.8-19.6 19.7-19.6h96.6zm-267.1-70.6a19.7 19.7 0 0 1-27.9 27.8l-68.3-68.3a19.7 19.7 0 0 1 0-27.9 19.6 19.6 0 0 1 27.8 0l68.4 68.4zm221.1 221.1a19.7 19.7 0 0 1 0 27.9 19.5 19.5 0 0 1-27.8-.1l-68.3-68.3a19.7 19.7 0 0 1 0-27.9 19.6 19.6 0 0 1 27.8 0l68.3 68.4zm-248.9-68.3a19.7 19.7 0 0 1 27.9 27.9l-68.3 68.3a19.5 19.5 0 0 1-27.8-.1 19.6 19.6 0 0 1 0-27.8l68.2-68.3zm221.1-221.1a19.7 19.7 0 0 1 27.8 27.9l-68.3 68.3a19.6 19.6 0 1 1-27.8-27.8l68.3-68.4z"/>
      </svg>

      <!-- clearButtonIcon -->
      <span v-show="!disableInput && !isEmpty && !isLoading && !hasError" class="autocomplete__icon autocomplete--clear" @click="killem">
        <span v-if="clearButtonIcon" :class="clearButtonIcon"></span>
        <svg v-else xmlns="http://www.w3.org/2000/svg" viewBox="144 -154 1100 1100">
          <path fill="#999" d="M1158.7 53.6L816.3 396l342.4 342.4a86.5 86.5 0 0 1-122.3 122.3L694 518.3 351.6 860.7A86.3 86.3 0 0 1 204 799.5c0-23.9 9.7-45.5 25.3-61.1L571.7 396 229.3 53.6A86.3 86.3 0 0 1 290.5-94c23.9 0 45.5 9.7 61.1 25.3L694 273.7l342.4-342.4A86.3 86.3 0 0 1 1184-7.5c0 23.8-9.7 45.5-25.3 61.1z"/>
        </svg>
      </span>
    </div>

    <ul v-show="showResults" class="autocomplete__results" :style="listStyle">

      <slot name="results">
        <!-- error -->
        <li v-if="hasError" class="autocomplete__results__item autocomplete__results__item--error">{{ error }}</li>

        <!-- results -->
        <template v-if="!hasError">
          <slot name="firstResult"></slot>
          <li
              v-for="(result, key) in results"
              :key="key"
              @click.prevent="select(result)"
              class="autocomplete__results__item"
              :class="{'autocomplete__selected' : isSelected(key) }"
              v-html="formatDisplay(result)">
          </li>
          <slot name="lastResult"></slot>
        </template>

        <!-- no results -->
        <li v-if="noResults && !isLoading && isFocussed && !hasError && showNoResults" class="autocomplete__results__item autocomplete__no-results">Ничего не найдено.</li>
      </slot>
    </ul>
  </div>
</template>

<script type="text/babel">
  // https://github.com/charliekassel/vuejs-autocomplete

  import debounce from 'lodash/debounce'
  export default {
    model: {
        prop: 'modelValue',
        event: 'input'
        // event: 'change'
      },
    props: {

      /**
       * Data source for the results
       *   `String` is a url, typed input will be appended
       *   `Function` received typed input, and must return a string; to be used as a url
       *   `Array` and `Object` (see `results-property`) are used directly
       */
      source: {
        type: [String, Function, Array, Object],
        required: true
      },

      modelValue: {
        modelValue: {
          default:  function () { return [] }
        }
      },

      /**
       * Input placeholder
       */
      placeholder: {
        default: 'Search'
      },
      /**
       * Preset starting value
       */
      initialValue: {
        type: [String, Number]
      },
      /**
       * Preset starting display value
       */
      initialDisplay: {
        type: String
      },
      /**
       * CSS class for the surrounding input div
       */
      inputClass: {
        type: [String, Object]
      },
      /**
       * To disable the input
       */
      disableInput: {
        type: Boolean
      },
      /**
       * name property of the input holding the selected value
       */
      name: {
        type: String
      },
      /**
       * api - property of results array
       */
      resultsProperty: {
        type: String
      },
      /**
       * Results property used as the value
       */
      resultsValue: {
        type: String,
        default: 'id'
      },
      /**
       * Results property used as the display
       */
      resultsDisplay: {
        type: [String, Function],
        default: 'name'
      },

      /**
       * Whether to show the no results message
       */
      showNoResults: {
        type: Boolean,
        default: true
      },

      /**
       * Additional request headers
       */
      requestHeaders: {
        type: Object
      },

      /**
       * Optional clear button icon class
       */
      clearButtonIcon: {
        type: String
      }
    },
    data () {
      return {
        value: null,
        display: null,
        results: null,
        selectedIndex: null,
        loading: false,
        isFocussed: false,
        error: null,
        selectedId: null,
        selectedDisplay: null,
        eventListener: false
      }
    },
    computed: {
      showResults () {
        return Array.isArray(this.results) || this.hasError
      },
      noResults () {
        return Array.isArray(this.results) && this.results.length === 0
      },
      isEmpty () {
        return !this.modelValue.length
      },
      isLoading () {
        return this.loading === true
      },
      hasError () {
        return this.error !== null
      },
      listStyle () {
        if (this.isLoading) {
          return {
            color: '#ccc'
          }
        }
      }
    },
    methods: {
      focus () {
        console.log('this.isFocussed', this.isFocussed);
        if (this.isFocussed === true) {
          this.$emit('results');
          this.blur()
        } else {
          this.isFocussed = true
          this.$emit('focus')
        }
      },
      blur () {
        this.isFocussed = false
        // this.$emit('blur')
      },
      isSelected (key) {
        return key === this.selectedIndex
      },
      search () {
        this.display = this.$el.querySelector('input').value // FIXME: NOTE: Android FIX
        this.selectedIndex = null
        switch (typeof this.source) {
          case 'string':
            // No resource search with no input
            if (!this.display || this.display.length < 2) {
              return
            }
            this.loading = true

            this.resourceSearch(this.source + this.display)
            break
          case 'function':
            // No resource search with no input
            if (!this.display || this.display.length < 2) {
              return
            }
            this.loading = true

            this.resourceSearch(this.source(this.display))
            break
          case 'object':
            this.loading = true
            this.objectSearch()
            break
          default:
            throw new TypeError()
        }
      },

      resourceSearch: debounce(function (url) {
        if (!this.display) {
          this.results = []
          this.loading = false
          return
        }

        // query param should be a setting, rather than appended.
        let promise = fetch(url, {
          // method: 'get',
          // mode: 'no-cors',
          // credentials: 'same-origin',
          // headers: this.getHeaders()
        })

        this.setEventListener()

        promise
          .then(response => {
            if (response.ok) {
              this.error = null
              return response.json()
            }
            throw new Error('Network response was not ok.')
          })
          .then(response => {
            // this.results = this.setResults(response)
            let results = this.setResults(response)
            // HACK filter
            this.results = results;
            // this.results = results.filter((item) => {
            //   return this.formatDisplay(item).toLowerCase().includes(this.display.toLowerCase())
            // })
            if (this.results.length === 0) {
              this.$emit('noResults', {query: this.display})
            } else {
              // есть резалт
              this.$emit('results', {results: this.results})
            }
            this.loading = false
          })
          .catch(error => {
            this.error = error.message
            this.loading = false
          })
      }, 200),

      getHeaders () {
        const headers = {
          'Accept': 'application/json, text/plain, */*',
          'Content-Type': 'application/json'
        }

        if (this.requestHeaders) {
          for (var prop in this.requestHeaders) {
            headers[prop] = this.requestHeaders[prop]
          }
        }
        return new Headers(headers)
      },

      /**
       * Set results property from api response
       * @param {Object|Array} response
       * @return {Array}
       */
      setResults (response) {
        if (this.resultsProperty && response[this.resultsProperty]) {
          return response[this.resultsProperty]
        }
        if (Array.isArray(response)) {
          return response
        }
        return []
      },

      objectSearch () {
        this.setEventListener()

        if (!this.display) {
          this.results = this.source
          this.$emit('results', {results: this.results})
          this.loading = false
          return true
        }

        this.results = this.source.filter((item) => {
          return this.formatDisplay(item).toLowerCase().includes(this.display.toLowerCase())
        })

        // not v.dry :(
        this.$emit('results', {results: this.results})
        this.loading = false
      },

      select (obj) {
        if (!obj) {
          return
        }

        // this.setValues(obj)
        this.setArrayValues(obj)

        // this.$emit('selected', {
        //   value: this.value,
        //   display: this.display,
        //   selectedObject: obj
        // })

        // this.$emit('input', this.value)
        // this.$emit('input', [obj])
        this.clear()
        this.close()
      },

      setValues (obj) {
        this.value = (this.resultsValue && obj[this.resultsValue]) ? obj[this.resultsValue] : obj.id
        this.display = this.formatDisplay(obj)
        this.selectedDisplay = this.display
      },

      setArrayValues (obj) {
        let value;
        if (Array.isArray(this.modelValue)) {
          value = this.modelValue.slice(0);
          value.push(obj);
          // TODO: DUPLICATES CHECK
        } else {
          value = [];
        }
        this.$emit('input', value);
      },

      /**
       * @param  {Object} obj
       * @return {String}
       */
      formatDisplay (obj) {
        switch (typeof this.resultsDisplay) {
          case 'function':
            // console.log('obj descr', obj);
            // return this.$parent.formatDisplay(obj[this.resultsDisplay])
            return this.resultsDisplay(obj)
          case 'string':
            if (obj[this.resultsDisplay]) {
              // return obj['pfx'] + ' ' + obj[this.resultsDisplay] + ' ' + obj['sfx']
              return obj[this.resultsDisplay]  // TODO:  HACK: !!!!!!!!!!!!!!!!!!!!!
            } else {
              let msg = '"' + this.resultsDisplay + '"' + ' property expected on result but is not defined.'
              throw new Error(msg)
            }
          default:
            throw new TypeError()
        }
      },

      up () {
        if (this.selectedIndex === null) {
          this.selectedIndex = this.results.length - 1
          return
        }
        this.selectedIndex = (this.selectedIndex === 0) ? this.results.length - 1 : this.selectedIndex - 1
      },
      down () {
        if (this.selectedIndex === null) {
          this.selectedIndex = 0
          return
        }
        this.selectedIndex = (this.selectedIndex === this.results.length - 1) ? 0 : this.selectedIndex + 1
      },
      enter () {
        if (this.selectedIndex === null) {
          // this.down();
          this.selectedIndex = 0;
          // this.$emit('input', this.results[0])
          // this.$emit('enter', this.display)
          // return
        }
        this.$emit('focus')
        this.select(this.results[this.selectedIndex])
      },
      killem () {
        this.$emit('clear')
      },
      clear () {
        this.clearValues()
        this.results = null
        this.error = null
      },
      clearValues () {
        this.display = null
        this.value = null
        this.results = null
      },
      close () {
        if (!this.value || !this.selectedDisplay) {
          this.clear()
        }
        if (this.selectedDisplay !== this.display && this.value) {
          this.display = this.selectedDisplay
        }

        this.results = null
        this.error = null
        this.removeEventListener()
        this.$emit('close')
      },
      setEventListener () {
        if (this.eventListener) {
          return false
        }
        this.eventListener = true
        document.addEventListener('click', this.clickOutsideListener, true)
        return true
      },
      removeEventListener () {
        this.eventListener = false
        document.removeEventListener('click', this.clickOutsideListener, true)
      },
      clickOutsideListener (event) {
        if (this.$el && !this.$el.contains(event.target)) {
          this.close()
        }
      }
    },
    mounted () {
      this.value = this.initialValue
      this.display = this.initialDisplay
      this.selectedDisplay = this.initialDisplay
    }
  }
</script>

<style lang="scss">


  .autocomplete {
    position: relative;
    width: 100%;
    * {
      box-sizing: border-box;
    }
  }

  .autocomplete__box {
    display: flex;
    align-items: center;
    background: #fafafa;
    border: 1px solid #e7e7e7;
    padding: 0 15px 0 0;
  }

  // .autocomplete__searching {
    // border-radius: 3px 3px 0 0;
  // }

  .autocomplete__inputs {
    flex-grow: 1;
    padding: 0 5px 0 0;
    input {
      width: 100%;
      border: 0;
      // height: 38px;
      text-overflow: ellipsis !important;
      &:focus {
        outline: none;
        box-shadow: none !important;
        text-overflow: ellipsis !important;
      }
      &:hover {
        box-shadow: none !important;
      }
      &[placeholder]{
          text-overflow: ellipsis;
      }
    }
  }

  .autocomplete--clear {
    cursor: pointer;
    width: 12px;
    height: 12px;
    margin-right: -4px;
  }

  .autocomplete__results {
    margin: 3px 0 0;
    padding: 0;
    list-style-type: none;
    z-index: 1000;
    position: absolute;
    max-height: 400px;
    overflow-y: auto;
    background: #fff;
    width: 100%;
    border: 1px solid #e7e7e7;
    border-top: 0;
    box-shadow: 0 2px 10px 0px rgba(0, 0, 0, 0.2);
  }

  .autocomplete__results__item--error {
    color: #f00;
  }

  .autocomplete__results__item {
    padding: 7px 10px;
    cursor: pointer;
    color: #666;
    &:hover {
      background: #e7e7e7;
    }
    &.autocomplete__selected {
      background: #e7e7e7;
    }
  }

  .autocomplete__icon {
    height: 16px;
    width: 16px;
    svg{
      height: 90%;
    }
  }
  .shevron{
      width: 12px;
      display: inline-block;
      vertical-align: middle;
      height: 20px;
      background-image: url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAxMS44IDYuOSI+CiAgPHBhdGggZmlsbD0iIzljOWQ5ZSIgZD0iTTUuOSA3YTEgMSAwIDAgMS0uNy0uNGwtNS00LjlBMSAxIDAgMCAxIC4zLjNhMSAxIDAgMCAxIDEuNSAwTDYgNC41IDEwLjEuM2ExIDEgMCAwIDEgMS40IDBjLjQuNC40IDEgMCAxLjRsLTUgNWExIDEgMCAwIDEtLjYuMnoiLz4KPC9zdmc+");
      background-position: 50% 50%;
      // background-size: 130%;
      background-repeat: no-repeat;
    }
  .active .shevron{
    transform: scale(-1);
    transition: all .35s ease;
  }

  .animate-spin {
    animation: spin 2s infinite linear;
  }

  @-moz-keyframes spin {
    from {
      transform: rotate(0deg);
    }

    to {
      transform: rotate(360deg);
    }
  }


  @-webkit-keyframes spin {
    from {
      transform: rotate(0deg);
    }

    to {
      transform: rotate(360deg);
    }
  }


  @-o-keyframes spin {
    from {
      transform: rotate(0deg);
    }

    to {
      transform: rotate(360deg);
    }
  }


  @keyframes spin {
    from {
      transform: rotate(0deg);
    }

    to {
      transform: rotate(360deg);
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/geo/autocomplete.vue